import React, { useEffect, useState } from 'react';
import { EntityService } from 'core/api/services/lookUp';
import { useUnits } from 'core/contexts/units';
import { useTranslation } from 'react-i18next';
import { isValidPositiveInteger } from '@utriper/core';
import { Field, Form } from 'react-final-form';
import { AlertDialog, LookUpUnit, NumberInput, Switch } from '@utriper/core-ui';
import { Box, Button, CircularProgress, FormHelperText, Grid2, Typography } from '@mui/material';
import { Container, Label } from './styles';

const UnitLinkForm = ({ unitLinkId = null, onSubmit = () => {}, open = false, submitting = false, onClose = () => {}, initialValues, unitsCreated = [] }) => {
    const isEdit = isValidPositiveInteger(unitLinkId);
    const [submitted, setSubmitted] = useState(false);
    const { t } = useTranslation();
    const translate = (key, options) => t(`unit_link_form.${key}`, options);
    const { getUnits, units } = useUnits();

    useEffect(() => {
        getUnits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateForm = (values) => {
        const errors = {};

        const minPerBooking = parseInt(values?.minPerBooking);
        const maxPerBooking = parseInt(values?.maxPerBooking);
        const capacity = parseInt(values?.capacity);

        if (!isValidPositiveInteger(values?.unitId)) {
            errors.unitId = t('common_form.required');
        }

        const unitData = units?.find((unit) => unit?.id === values?.unitId);
        const oldUnit = units?.find((unit) => unit?.id === initialValues?.unitId);

        if (unitData?.id && oldUnit?.id && unitData?.id !== oldUnit?.unitId) {
            const newUnitType = unitData.typeId;
            const oldUnitType = oldUnit.typeId;

            if (newUnitType !== oldUnitType) {
                errors.unitId = translate('errors.invalid_type');
            }
        }

        if (unitsCreated?.length > 0 && unitData?.id && unitsCreated?.some((unit) => unit?.type === unitData?.typeId && unit?.unitId !== unitData?.id)) {
            errors.unitId = translate('errors.invalid_id');
        }

        if (!isNaN(minPerBooking) && !isNaN(maxPerBooking) && minPerBooking > maxPerBooking) {
            errors.minPerBooking = translate('errors.invalid_min_per_booking', { maxPerBooking: values.maxPerBooking });
        }

        if (!isNaN(capacity) && (minPerBooking > capacity || maxPerBooking > capacity)) {
            errors.capacity = translate('errors.invalid_capacity');
        }

        return errors;
    };

    return (
        <AlertDialog
            open={open}
            handleClose={onClose}
            hideTitle
            hideActions
            maxWidth="sm"
            fullWidth
            dialogContent={
                <Form
                    key={`unit-link-form-${unitLinkId ?? 0}`}
                    initialValues={initialValues}
                    validate={validateForm}
                    onSubmit={onSubmit}
                    render={({ values, handleSubmit, form, errors }) => {
                        const handleActive = (event) => {
                            const checked = event?.target?.checked;

                            form.change('isActive', checked);
                        };

                        const handleUnit = (unitId) => {
                            const unitData = units?.find((unit) => unit?.id === unitId);

                            if (Number.isInteger(unitData?.typeId)) {
                                form.change('type', unitData?.typeId);
                            }

                            form.change('unitId', unitId);
                        };

                        return (
                            <form key={`unit-link-form-${unitLinkId ?? 0}`} onSubmit={handleSubmit} noValidate>
                                <Box component="section" py={2}>
                                    {/* Title */}
                                    <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                        <Typography fontSize={20} fontWeight={900} sx={{ color: '#131313' }} noWrap>
                                            {isEdit ? translate('edit_title', { name: values?.name }) : translate('add_title')}
                                        </Typography>
                                        <Typography fontSize={16} fontWeight={500} sx={{ color: '#131313bf' }}>
                                            {translate('subtitle')}
                                        </Typography>
                                    </Grid2>

                                    {/* Form */}
                                    <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                        {/* Unit ID */}
                                        <Container sx={{ '& .MuiAutocomplete-root': { width: '100%' } }}>
                                            <Label>{translate('unit_id_label')}</Label>
                                            <Field id="unitId" name="unitId" render={({ input }) => <LookUpUnit {...input} label={translate('unit_id_placeholder')} entityService={EntityService} value={values?.unitId} initialValue={values?.unitId} onChange={handleUnit} />} />
                                            {submitted && errors?.unitId && <FormHelperText error>{errors.unitId}</FormHelperText>}
                                        </Container>

                                        {/* Capacity */}
                                        <Container>
                                            <Label>{translate('capacity_label')}</Label>
                                            <Field name="capacity" component={NumberInput} label={translate('capacity_label')} placeholder={translate('capacity_placeholder')} />
                                        </Container>

                                        {/* Restrictions */}
                                        <Container>
                                            <Label>{translate('restrictions_label')}</Label>
                                            <Grid2 container size={12} spacing={1}>
                                                <Grid2 container size={6}>
                                                    <Field name="minPerBooking" component={NumberInput} label={translate('min_per_booking_label')} placeholder={translate('min_per_booking_placeholder')} />
                                                </Grid2>
                                                <Grid2 container size={6}>
                                                    <Field name="maxPerBooking" component={NumberInput} label={translate('max_per_booking_label')} placeholder={translate('max_per_booking_placeholder')} />
                                                </Grid2>
                                            </Grid2>
                                        </Container>

                                        {/* Is Active */}
                                        <Container
                                            sx={{
                                                '& .MuiSwitch-switchBase': {
                                                    '&.Mui-checked': {
                                                        color: '#fff',
                                                        '& + .MuiSwitch-track': {
                                                            backgroundColor: '#137DC5',
                                                            opacity: 1,
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <Field type="checkbox" name="isActive" component={Switch} label={translate('is_active_label')} input={{ checked: values?.isActive ?? false, onChange: handleActive }} />
                                        </Container>

                                        <FormHelperText>{translate('unlimited_capacity_helper')}</FormHelperText>
                                    </Grid2>

                                    {/* Action Buttons */}
                                    <Grid2 container justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems="center" gap="12px" mt="16px">
                                        <Button
                                            sx={{
                                                padding: '8px 14px',
                                                borderColor: '#13131340',
                                                fontWeight: 700,
                                                width: { xs: '100%', md: 'fit-content' },
                                                textTransform: 'none',
                                            }}
                                            variant="outlined"
                                            disabled={submitting}
                                            onClick={(e) => {
                                                onClose();
                                                return form.reset(e);
                                            }}
                                        >
                                            {translate('cancel')}
                                        </Button>
                                        <Button
                                            sx={{
                                                padding: '8px 14px',
                                                backgroundColor: 'var(--color-utriper-blue)',
                                                fontWeight: 700,
                                                width: { xs: '100%', md: 'fit-content' },
                                                textTransform: 'none',
                                            }}
                                            loading={submitting}
                                            loadingIndicator={
                                                <Box sx={{ display: 'flex' }}>
                                                    <CircularProgress size={20} sx={{ color: '#131313bf' }} />
                                                </Box>
                                            }
                                            variant="contained"
                                            type="submit"
                                            onClick={(e) => {
                                                setSubmitted(true);
                                                return handleSubmit(e);
                                            }}
                                        >
                                            {isEdit ? translate('edit_button') : translate('add_button')}
                                        </Button>
                                    </Grid2>
                                </Box>
                            </form>
                        );
                    }}
                />
            }
        />
    );
};

export default UnitLinkForm;
